import { useState } from "react";
import { Autocomplete, Badge, Box, Button, Card, Group, Skeleton, Stack, Text, Textarea } from "@mantine/core";
import { IconExternalLink, IconRefresh } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { fmtNumber, humanizedToShoppingItems, shoppingListText, type ShoppingItem } from "@passio/api-client";
import { passio } from "../../lib/clients";
import { composeMessage } from "../../lib/clientSettings";
import { useSession } from "../../lib/session";
import { ErrorNotice } from "../../components/common";
import { MealGroupsView } from "../../components/food";
import { GOAL_PRESETS, GoalRatingResult } from "../../components/GoalRating";
import type { ShoppingHandoff } from "../Shopping";
import { foodsToShoppingItems, nameOf, PickList, RunBar, Step, togglePick, TotalsCompare, TourTimeline, type TourFood } from "./shared";

const MEALS = [
  "Lunch was white rice with teriyaki chicken, a side of edamame and a can of Coke",
  "Breakfast: a glazed donut, a banana and a large orange juice",
  "Dinner: pepperoni pizza, garlic bread and a caesar salad",
];

const giColor = (gi: number) => (gi < 56 ? "lime.8" : gi < 70 ? "yellow.7" : "red.6");
const mid = (a?: number, b?: number) => ((a ?? 0) + (b ?? a ?? 0)) / 2;

/**
 * A typed meal through the platform: extract it, fill in what the user didn't say, score it, rate it
 * for their goal and turn it into a cart. Every step is a plain Passio API call.
 */
export function TextTour({ onSelect }: { onSelect: (f: TourFood) => void }) {
  const { hasFeature } = useSession();
  const navigate = useNavigate();
  const [meal, setMeal] = useState(MEALS[0]);
  const [goal, setGoal] = useState(GOAL_PRESETS[0]);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const extract = useMutation({ mutationFn: (t: string) => passio.tools.extractIngredients(t, composeMessage("")) });
  const hidden = useMutation({ mutationFn: (foods: string) => passio.tools.invisibleIngredientSuggestions(foods) });
  const gi = useMutation({ mutationFn: (t: string) => passio.tools.estimateGlycemicIndexLoad(t) });
  const upf = useMutation({ mutationFn: (t: string) => passio.tools.ultraProcessedFoodRating(t) });
  const rate = useMutation({ mutationFn: (v: { food: string; goal: string }) => passio.tools.genericFoodRating(v.food, v.goal) });
  const humanize = useMutation({ mutationFn: (items: ShoppingItem[]) => passio.tools.humanizeShoppingList(shoppingListText(items)) });
  const cart = useMutation({ mutationFn: (items: ShoppingItem[]) => passio.affiliates.convertShoppingList(items, { title: "Passio guided tour", campaign: "passio-demo-tour", sourceTool: "passio-demo" }) });
  const [running, setRunning] = useState(false);

  const can = { insights: hasFeature("insights"), shop: hasFeature("shopping") };
  const foods = extract.data?.data ?? [];
  const extras = hidden.data?.data ?? [];
  const chosen = extras.filter((_, i) => picked.has(i));
  const giRows = gi.data?.data.ingredients ?? [];
  const highlighted = upf.data?.data.highlightedIngredients ?? [];
  const cartList = humanizedToShoppingItems(humanize.data?.data);

  const reset = () => {
    [extract, hidden, gi, upf, rate, humanize, cart].forEach((m) => m.reset());
    setPicked(new Set());
  };

  const shop = async (items: TourFood[]) => {
    const raw = foodsToShoppingItems(items);
    if (!raw.length) return;
    const list = humanizedToShoppingItems((await humanize.mutateAsync(raw)).data);
    await cart.mutateAsync(list.length ? list : raw);
  };

  const runAll = async () => {
    reset();
    setRunning(true);
    try {
      const ex = (await extract.mutateAsync(meal)).data;
      const names = ex.map(nameOf).join(", ") || meal;
      await Promise.allSettled([
        hidden.mutateAsync(names),
        ...(can.insights ? [gi.mutateAsync(meal), upf.mutateAsync(meal)] : []),
        ...(can.insights && goal.trim() ? [rate.mutateAsync({ food: meal, goal: goal.trim() })] : []),
      ]);
      if (can.shop && ex.length) await shop(ex);
    } catch {
      // Each step shows its own error.
    } finally {
      setRunning(false);
    }
  };

  const rating = upf.data?.data.rating;
  const busy = running || extract.isPending;
  const active = cart.data ? 5 : rate.data ? 4 : gi.data || upf.data ? 3 : hidden.data ? 2 : extract.data ? 1 : 0;

  return (
    <Stack gap="lg">
      <Card p={{ base: "md", sm: "xl" }}>
        <Stack gap="sm">
          <Textarea label="What did you eat?" autosize minRows={2} size="md" value={meal} onChange={(e) => setMeal(e.currentTarget.value)} disabled={busy} />
          <Group gap={6}>
            {MEALS.map((m) => (
              <Badge key={m} variant="outline" color="gray" tt="none" style={{ cursor: "pointer" }} onClick={() => !busy && setMeal(m)}>
                {m.length > 48 ? `${m.slice(0, 48)}…` : m}
              </Badge>
            ))}
          </Group>
          <Autocomplete label="Your goal" description="Any criterion works; step 4 rates the meal against it." data={GOAL_PRESETS} value={goal} onChange={setGoal} disabled={busy} />
          <RunBar busy={busy} canRun={Boolean(meal.trim())} hasResults={active > 0} onRun={runAll} onClear={reset} />
        </Stack>
      </Card>

      <TourTimeline
        active={active}
        steps={[
          {
            label: "Recognize",
            body: (
              <Step title="Text to foods" route="POST /napi/tools/extractIngredients" to="/text" done={Boolean(extract.data)}>
                {extract.error ? <ErrorNotice error={extract.error} /> : null}
                {extract.isPending && <Skeleton h={120} radius="md" />}
                {extract.data && !extract.isPending && <MealGroupsView items={foods} onSelect={onSelect} />}
                {!extract.data && !extract.isPending && !extract.error && (
                  <Text fz="sm" c="dimmed">
                    Free text becomes a list of foods, each matched to a Passio food with nutrition, portion and grams.
                  </Text>
                )}
              </Step>
            ),
          },
          {
            label: "Fill in what wasn't said",
            body: (
              <Step title="Hidden ingredients" route="POST /napi/tools/invisibleIngredientSuggestions" to="/dish" done={Boolean(hidden.data)}>
                {hidden.error ? <ErrorNotice error={hidden.error} /> : null}
                {hidden.isPending && <Skeleton h={100} radius="md" />}
                {hidden.data && !hidden.isPending && (
                  <Stack gap="sm">
                    <Text fz="sm" c="dimmed">
                      The foods from step 1 went in as “{foods.map(nameOf).join(", ")}”. These are usually there but rarely mentioned: cooking oil, sauces, sugar. Tick the ones the user confirms.
                    </Text>
                    {extras.length ? <PickList items={extras} picked={picked} onToggle={(i) => setPicked((s) => togglePick(s, i))} onOpen={onSelect} /> : <Text c="dimmed">Nothing hidden suggested.</Text>}
                    <TotalsCompare before={foods} after={[...foods, ...chosen]} beforeLabel="As typed" afterLabel="With confirmed extras" />
                  </Stack>
                )}
                {!hidden.data && !hidden.isPending && !hidden.error && (
                  <Text fz="sm" c="dimmed">
                    Users say “teriyaki chicken”, not “teriyaki chicken cooked in a tablespoon of oil”. This asks for what's likely missing so the log is closer to reality.
                  </Text>
                )}
              </Step>
            ),
          },
          {
            label: "Understand",
            body: (
              <Step title="Glycemic load and processing" route="POST /napi/tools/estimateGlycemicIndexLoad · /ultraProcessedFoodRating" to="/insights" done={Boolean(gi.data || upf.data)} locked={can.insights ? undefined : "Food insights aren't enabled for this account."}>
                {gi.error ? <ErrorNotice error={gi.error} /> : null}
                {(gi.isPending || upf.isPending) && <Skeleton h={100} radius="md" />}
                {(gi.data || upf.data) && !gi.isPending && !upf.isPending ? (
                  <Group align="flex-start" gap="xl" wrap="wrap">
                    <Stack gap={6} style={{ flex: 1, minWidth: 220 }}>
                      {giRows.map((r, i) => (
                        <Group key={i} justify="space-between" wrap="nowrap">
                          <Text fz="sm" fw={600} truncate>
                            {r.name}
                          </Text>
                          <Group gap={6} wrap="nowrap">
                            <Badge size="sm" variant="light" color={giColor(mid(r.gi_low, r.gi_high))}>
                              GI {fmtNumber(mid(r.gi_low, r.gi_high))}
                            </Badge>
                            <Text fz="xs" ff="monospace" c="dimmed">
                              GL {fmtNumber(mid(r.gl_low, r.gl_high), 1)}
                            </Text>
                          </Group>
                        </Group>
                      ))}
                    </Stack>
                    {rating !== undefined && !upf.data?.data.isSupplement && (
                      <Box ta="center" p="md" style={{ background: "var(--pd-cream)", borderRadius: 14, minWidth: 150 }}>
                        <Text fz={36} fw={800} lh={1} c={rating <= 3 ? "lime.8" : rating <= 6 ? "yellow.8" : "red.7"}>
                          {rating}
                          <Text span fz="sm" c="dimmed" fw={500}>
                            {" "}
                            / 10
                          </Text>
                        </Text>
                        <Text fz="xs" c="dimmed" mt={4}>
                          processing score
                        </Text>
                        {highlighted.length > 0 && (
                          <Text fz="xs" mt={6}>
                            Driven by {highlighted.slice(0, 3).join(", ")}
                          </Text>
                        )}
                      </Box>
                    )}
                  </Group>
                ) : null}
                {!gi.data && !upf.data && !gi.isPending && !gi.error && (
                  <Text fz="sm" c="dimmed">
                    Glycemic index and load ranges per ingredient, and a 1–10 score for how processed the meal is, with the reasons.
                  </Text>
                )}
              </Step>
            ),
          },
          {
            label: "Rate it for the user's goal",
            body: (
              <Step title="Rate against any criterion" route="POST /napi/tools/genericFoodRating" to="/insights" done={Boolean(rate.data)} locked={can.insights ? undefined : "Food insights aren't enabled for this account."}>
                {rate.error ? <ErrorNotice error={rate.error} /> : null}
                {rate.isPending && <Skeleton h={100} radius="md" />}
                {rate.data && !rate.isPending && <GoalRatingResult data={rate.data.data} criterion={rate.variables?.goal} size={100} />}
                {!rate.data && !rate.isPending && !rate.error && (
                  <Text fz="sm" c="dimmed">
                    One flexible rater instead of a model per goal: pass the food and a free-text criterion (diabetes, keto, kid-friendly…) and get 0–10 with the reasoning.
                  </Text>
                )}
              </Step>
            ),
          },
          {
            label: "Shop",
            body: (
              <Step title="Shopper-friendly list, then a cart" route="POST /napi/tools/humanizeShoppingList · /napi/affiliates/convertShoppingListToAffiliateLink" to="/shopping" done={Boolean(cart.data)} locked={can.shop ? undefined : "Shopping isn't enabled for this account."}>
                {humanize.error ? <ErrorNotice error={humanize.error} /> : null}
                {cart.error ? <ErrorNotice error={cart.error} /> : null}
                {(humanize.isPending || cart.isPending) && <Skeleton h={60} radius="md" />}
                {cart.data && !cart.isPending && !humanize.isPending && (
                  <Stack gap="sm">
                    <Text fz="sm" c="dimmed">
                      The foods, in the units a shopper buys:
                    </Text>
                    <Group gap={6}>
                      {cartList.map((i, n) => (
                        <Badge key={`${i.name}-${n}`} variant="light" color="gray" tt="none">
                          {[i.portionQuantity, i.portionSize, i.name].filter(Boolean).join(" ")}
                        </Badge>
                      ))}
                    </Group>
                    <Group gap="xs">
                      <Button component="a" href={cart.data.data.passio_redirect_url} target="_blank" rel="noreferrer" rightSection={<IconExternalLink size={16} />}>
                        Open the {(cart.data.data.affiliate ?? "instacart").replace(/^\w/, (c) => c.toUpperCase())} cart
                      </Button>
                      {chosen.length > 0 && (
                        <Button variant="light" color="violet" leftSection={<IconRefresh size={16} />} onClick={() => void shop([...foods, ...chosen]).catch(() => undefined)}>
                          Rebuild with the {chosen.length} extra{chosen.length > 1 ? "s" : ""}
                        </Button>
                      )}
                      <Button variant="default" onClick={() => navigate("/shopping", { state: { items: cartList, title: "Passio guided tour" } satisfies ShoppingHandoff })}>
                        Edit the list
                      </Button>
                    </Group>
                  </Stack>
                )}
                {!cart.data && !cart.isPending && !humanize.isPending && !cart.error && !humanize.error && (
                  <Text fz="sm" c="dimmed">
                    Make it at home: the foods become a shopping list in real store units (“1 bag of rice”, not “180 g”), then a tracked Instacart link.
                  </Text>
                )}
              </Step>
            ),
          },
        ]}
      />
    </Stack>
  );
}
